/**
 * React hooks for API requests
 *
 * Features:
 * - Loading states
 * - Error handling
 * - Automatic cleanup
 * - TypeScript support
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as apiClient from "~/utils/apiClient";
import { ApiError, ApiRequestConfig } from "~/types-and-enums/apiTypes";

export type UseApiState<T> = {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
};

export type UseApiReturn<T> = UseApiState<T> & {
  execute: () => Promise<void>;
  reset: () => void;
};

/**
 * Hook for GET requests with automatic execution
 */
export function useGet<T = unknown>(
  endpoint: string,
  config?: Omit<ApiRequestConfig, "method" | "body">,
  options?: {
    immediate?: boolean;
    dependencies?: unknown[];
  }
): UseApiReturn<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: options?.immediate !== false,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const execute = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiClient.get<T>(endpoint, {
        ...config,
        signal: abortControllerRef.current.signal,
      });

      setState({
        data: response.data,
        loading: false,
        error: null,
      });
    } catch (error) {
      // Ignore abort errors
      if ((error as Error).name === "AbortError") {
        return;
      }

      setState({
        data: null,
        loading: false,
        error: error as ApiError,
      });
    }
  }, [endpoint, config]);

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
    });
  }, []);

  const dependencyList = options?.dependencies ?? [];

  useEffect(() => {
    if (options?.immediate !== false) {
      const run = () => {
        void execute();
      };

      if (typeof queueMicrotask === "function") {
        queueMicrotask(run);
      } else {
        Promise.resolve().then(run);
      }
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, options?.immediate, ...dependencyList]);

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * Hook for POST, PUT, PATCH, DELETE requests (manual execution)
 */
export function useMutation<T = unknown, TBody = unknown>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  endpoint: string,
  config?: Omit<ApiRequestConfig, "method">
) {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const execute = useCallback(
    async (body?: TBody) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        let response;

        switch (method) {
          case "POST":
            response = await apiClient.post<T>(endpoint, body, {
              ...config,
              signal: abortControllerRef.current.signal,
            });
            break;
          case "PUT":
            response = await apiClient.put<T>(endpoint, body, {
              ...config,
              signal: abortControllerRef.current.signal,
            });
            break;
          case "PATCH":
            response = await apiClient.patch<T>(endpoint, body, {
              ...config,
              signal: abortControllerRef.current.signal,
            });
            break;
          case "DELETE":
            response = await apiClient.del<T>(endpoint, {
              ...config,
              signal: abortControllerRef.current.signal,
            });
            break;
        }

        setState({
          data: response.data,
          loading: false,
          error: null,
        });

        return response.data;
      } catch (error) {
        // Ignore abort errors
        if ((error as Error).name === "AbortError") {
          return;
        }

        const apiError = error as ApiError;
        setState({
          data: null,
          loading: false,
          error: apiError,
        });

        throw apiError;
      }
    },
    [endpoint, method, config]
  );

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}

/**
 * Hook for POST requests
 */
export function usePost<T = unknown, TBody = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, "method">) {
  return useMutation<T, TBody>("POST", endpoint, config);
}

/**
 * Hook for PUT requests
 */
export function usePut<T = unknown, TBody = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, "method">) {
  return useMutation<T, TBody>("PUT", endpoint, config);
}

/**
 * Hook for PATCH requests
 */
export function usePatch<T = unknown, TBody = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, "method">) {
  return useMutation<T, TBody>("PATCH", endpoint, config);
}

/**
 * Hook for DELETE requests
 */
export function useDelete<T = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, "method" | "body">) {
  return useMutation<T, never>("DELETE", endpoint, config);
}

/**
 * Hook for file uploads
 */
export function useFileUpload<T = unknown>() {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const [progress, setProgress] = useState<number>(0);

  const upload = useCallback(
    async (
      endpoint: string,
      file: File | File[],
      additionalData?: Record<string, unknown>,
      config?: Omit<ApiRequestConfig, "method" | "body" | "contentType">
    ) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      setProgress(0);

      try {
        const response = await apiClient.uploadFile<T>(
          endpoint,
          {
            file,
            additionalData,
            onProgress: setProgress,
          },
          config
        );

        setState({
          data: response.data,
          loading: false,
          error: null,
        });

        return response.data;
      } catch (error) {
        const apiError = error as ApiError;
        setState({
          data: null,
          loading: false,
          error: apiError,
        });
        throw apiError;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
    });
    setProgress(0);
  }, []);

  return {
    ...state,
    progress,
    upload,
    reset,
  };
}
